import React, { useState } from 'react';
import { ArrowLeft, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { clearDashboardCache } from '../utils/dashboardCache';

const API = '/api';
const IMPERSONATOR_EMAIL = 'nimrod.lev@gmail.com';

// Drop every per-user dashboard cache so the next identity never renders
// the previous one's bookmarks (sessionStorage back-nav list + localStorage snapshot).
function clearUserCaches() {
    clearDashboardCache();
    try {
        Object.keys(sessionStorage)
            .filter((k) => k.startsWith('dashboard_'))
            .forEach((k) => sessionStorage.removeItem(k));
    } catch (_) { /* storage disabled */ }
}

// Full reload so every cached screen/state re-fetches as the new identity.
async function switchSession(url, target, label) {
    const res = await fetch(url, { method: 'POST', credentials: 'include' });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : `${label} failed`);
    }
    const body = await res.json().catch(() => ({}));
    clearUserCaches();
    window.location.assign(typeof target === 'function' ? target(body) : target);
}

export function ImpersonateButton({ userId }) {
    const { user } = useAuth();
    const [busy, setBusy] = useState(false);
    if (user?.email !== IMPERSONATOR_EMAIL || user?.impersonation || user?.user_id === userId) return null;

    const start = async () => {
        setBusy(true);
        try {
            await switchSession(`${API}/admin/impersonate/${userId}`, '/dashboard', 'Impersonate');
        } catch (e) {
            toast.error(e.message);
            setBusy(false);
        }
    };

    return (
        <Button size="sm" variant="outline" onClick={start} disabled={busy} className="shrink-0 h-7 px-2.5 text-xs" data-testid="admin-impersonate-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            Impersonate
        </Button>
    );
}

export function BackToAdminButton() {
    const { user } = useAuth();
    const [busy, setBusy] = useState(false);
    if (!user?.impersonation) return null;

    const stop = async () => {
        setBusy(true);
        try {
            await switchSession(
                `${API}/admin/impersonate/stop`,
                (body) => (body.restored ? `/admin/users/${user.user_id}` : '/'),
                'Back to Admin',
            );
        } catch (e) {
            toast.error(e.message);
            setBusy(false);
        }
    };

    return (
        <Button size="sm" variant="outline" onClick={stop} disabled={busy} className="shrink-0 h-7 px-2.5 text-xs self-center" data-testid="back-to-admin-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowLeft className="w-3.5 h-3.5" />}
            Back to Admin
        </Button>
    );
}
